import type {
  Experiment,
  ExperimentAssignment,
  ExperimentOutcome,
} from "@andrum/schemas";
import { MOCK_EXPERIMENTS } from "@/features/experiments/data/mock-experiments";
import { t } from "@/i18n";

export type ToolboxItem = {
  experimentId: string;
  title: string;
  category: string;
  timesCompleted: number;
  lastCompletedAt: string | null;
  avgRepeatIntent: number | null;
  note: string | null;
};

export type SimpleInsight = {
  id: string;
  text: string;
  type: "pattern" | "encouragement" | "empty";
};

export function buildToolbox(
  outcomes: ExperimentOutcome[],
  assignments: ExperimentAssignment[],
  experiments: Experiment[] = MOCK_EXPERIMENTS
): ToolboxItem[] {
  const byExp = new Map<string, ToolboxItem>();
  const catalog = experiments.length > 0 ? experiments : MOCK_EXPERIMENTS;

  for (const outcome of outcomes) {
    if (!outcome.completed) continue;
    const assignment = assignments.find((a) => a.id === outcome.assignmentId);
    if (!assignment) continue;
    const exp = catalog.find((e) => e.id === assignment.experimentId);
    if (!exp) continue;

    const existing = byExp.get(exp.id);
    if (existing) {
      existing.timesCompleted += 1;
      if (outcome.repeatIntent != null) {
        existing.avgRepeatIntent =
          existing.avgRepeatIntent == null
            ? outcome.repeatIntent
            : (existing.avgRepeatIntent + outcome.repeatIntent) / 2;
      }
      if (assignment.completedAt) {
        existing.lastCompletedAt = assignment.completedAt;
      }
      if (outcome.reflection) {
        existing.note = outcome.reflection;
      }
    } else {
      byExp.set(exp.id, {
        experimentId: exp.id,
        title: exp.title,
        category: exp.category,
        timesCompleted: 1,
        lastCompletedAt: assignment.completedAt,
        avgRepeatIntent: outcome.repeatIntent,
        note: outcome.reflection ?? null,
      });
    }
  }

  return Array.from(byExp.values()).sort(
    (a, b) => b.timesCompleted - a.timesCompleted
  );
}

export function deriveSimpleInsights(
  outcomes: ExperimentOutcome[],
  toolbox: ToolboxItem[]
): SimpleInsight[] {
  const insights: SimpleInsight[] = [];

  if (outcomes.length === 0) {
    insights.push({
      id: "empty",
      type: "empty",
      text: t("insights.emptyInsight"),
    });
    return insights;
  }

  const completed = outcomes.filter((o) => o.completed).length;
  insights.push({
    id: "count",
    type: "encouragement",
    text: t("insights.countInsight", { count: completed }),
  });

  const liked = toolbox.filter(
    (item) => item.avgRepeatIntent != null && item.avgRepeatIntent >= 1.5
  );
  if (liked.length > 0) {
    const top = liked[0]!;
    insights.push({
      id: "liked",
      type: "pattern",
      text: t("insights.likedInsight", { title: top.title }),
    });
  }

  const withMood = outcomes.filter((o) => o.afterMood != null);
  if (withMood.length >= 2) {
    const avg =
      withMood.reduce((s, o) => s + (o.afterMood ?? 0), 0) / withMood.length;
    if (avg >= 3) {
      insights.push({
        id: "mood",
        type: "pattern",
        text: t("insights.moodInsight"),
      });
    }
  }

  return insights;
}
